import { applyLayerFitMode } from '@/lib/layers/applyLayerFitMode';
import { fitRectToAspect } from '@/lib/layers/fitRectToAspect';
import type { LayerRect } from '@/lib/layers/types';
import { stockItemToLayerInput } from '@/lib/stock-media/stockItemToLayerInput';
import type { StockMediaItem } from '@/lib/stock-media/types';
import type { MyUserMediaRow } from '@/lib/stock-media/userMediaToStockItem';
import type { SoundEffectRow } from '@/queries/soundEffects';
import type { AssistantMediaItem } from './mediaTypes';

// editor_search_media results as the model sees them, and where a placed
// image/video goes in the frame. Rects are composition fractions, so a rect's
// displayed aspect is (w/h)·compAspect.

const MAX_ITEMS = 8;
const MAX_THUMBNAILS = 4;
const PIP_WIDTH = 0.38;
const PIP_MAX_HEIGHT = 0.5;
const PIP_MARGIN_X = 0.04;
const PIP_TOP = 0.06;
const MIN_RECT_SIDE = 0.02;
const FULL_FRAME: LayerRect = { x: 0, y: 0, w: 1, h: 1 };

export const MEDIA_PLACEMENTS = ['fill', 'fit', 'pip', 'rect'] as const;
export type MediaPlacement = (typeof MEDIA_PLACEMENTS)[number];

/** Up to 8 items; the first 4 thumbnails go back as images, and each item
 *  names its thumbnail by position (1-4) so the model can match them. */
export function formatMediaResults(items: readonly AssistantMediaItem[]): {
  items: Array<Record<string, unknown>>;
  images: string[];
} {
  const images: string[] = [];
  const rows = items.slice(0, MAX_ITEMS).map((entry) => {
    const row: Record<string, unknown> = entry.provider === 'sfx'
      ? {
        item_id: entry.id,
        kind: entry.kind,
        name: entry.name,
        source: 'sfx',
        category: entry.category,
        tags: entry.tags,
        duration: entry.duration,
        peak_at: entry.peakAt,
      }
      : {
        item_id: entry.id,
        kind: entry.kind,
        name: entry.name,
        source: entry.provider,
        width: entry.width,
        height: entry.height,
        duration: entry.duration,
      };
    if (entry.thumbnail && images.length < MAX_THUMBNAILS) {
      images.push(entry.thumbnail);
      row.thumbnail = images.length;
    }
    return row;
  });
  return { items: rows, images };
}

/** Where a visual media layer goes: fill (b-roll over the whole frame), fit
 *  (whole media inside the frame), pip (top-right corner) or a given rect
 *  (the media's aspect kept inside it). Without an aspect: the whole frame. */
export function placeMediaRect({ placement, rect, mediaAspect, compAspect }: {
  placement: MediaPlacement;
  rect?: LayerRect;
  mediaAspect: number | null;
  compAspect: number;
}): LayerRect {
  const ratio = mediaAspect && mediaAspect > 0 && compAspect > 0 ? mediaAspect / compAspect : null;
  switch (placement) {
    case 'fill':
    case 'fit':
      return ratio ? applyLayerFitMode({ rect: { x: 0, y: 0, w: ratio, h: 1 }, mode: placement }) : { ...FULL_FRAME };
    case 'pip': {
      let w = PIP_WIDTH;
      let h = ratio ? w / ratio : PIP_WIDTH;
      if (h > PIP_MAX_HEIGHT) {
        h = PIP_MAX_HEIGHT;
        w = ratio ? h * ratio : PIP_WIDTH;
      }
      return { x: 1 - PIP_MARGIN_X - w, y: PIP_TOP, w, h };
    }
    case 'rect': {
      const box = rect ?? FULL_FRAME;
      const clean: LayerRect = {
        x: box.x,
        y: box.y,
        w: Math.max(MIN_RECT_SIDE, box.w),
        h: Math.max(MIN_RECT_SIDE, box.h),
      };
      return ratio && mediaAspect ? fitRectToAspect({ rect: clean, mediaAspect, compAspect }) : clean;
    }
    default:
      return { ...FULL_FRAME };
  }
}

type StockInput = Pick<StockMediaItem, 'id' | 'provider' | 'kind' | 'thumbnail_url' | 'file_url' | 'width' | 'height'>
  & Partial<StockMediaItem>;

/** A Pexels/Unsplash search result. Ids carry the provider (they could collide). */
export function stockItemToAssistantMedia(item: StockInput): AssistantMediaItem {
  const input = stockItemToLayerInput(item as StockMediaItem);
  return {
    id: `${item.provider}-${item.id}`,
    kind: item.kind,
    url: item.file_url,
    thumbnail: item.thumbnail_url || null,
    width: item.width > 0 ? item.width : null,
    height: item.height > 0 ? item.height : null,
    duration: item.kind === 'video' ? (item.duration ?? null) : null,
    name: input.name,
    provider: item.provider,
    downloadLocation: item.download_location ?? null,
  };
}

/** A row of the user's My Media. Images are their own thumbnail; videos and
 *  audio have none. */
export function userMediaRowToAssistantMedia(row: MyUserMediaRow): AssistantMediaItem | null {
  if (row.kind !== 'image' && row.kind !== 'video' && row.kind !== 'audio') return null;
  const fileName = row.url.split('?')[0].split('/').pop() || row.url;
  return {
    id: `library-${row.id}`,
    kind: row.kind,
    url: row.url,
    thumbnail: row.kind === 'image' ? row.url : null,
    width: row.width && row.width > 0 ? row.width : null,
    height: row.height && row.height > 0 ? row.height : null,
    duration: row.kind === 'image' ? null : (row.duration ?? null),
    name: row.name || fileName,
    provider: 'library',
  };
}

/** A sound effect from the library: audio, no thumbnail, with its hit time. */
export function soundEffectToAssistantMedia(row: SoundEffectRow): AssistantMediaItem {
  return {
    id: `sfx-${row.id}`,
    kind: 'audio',
    url: row.url,
    thumbnail: null,
    width: null,
    height: null,
    duration: row.duration,
    name: row.name,
    provider: 'sfx',
    sfxId: row.id,
    category: row.category,
    tags: row.tags,
    peakAt: row.peak_at,
  };
}
